import { IonIcon } from '@ionic/react';
import { closeOutline as closeIcon } from 'ionicons/icons';
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';

import { SubscriptionStackItem } from './useInAppNotificationStack';

const STACK_OFFSET_Y = 10;
const STACK_SCALE_STEP = 0.035;

/** Lift subscription stack above `MessagesDisplay` (client stack at bottom: 20). */
const SUBSCRIPTION_VIEWPORT_BOTTOM_PX = 90;

/** Aligns with split-pane / content max-width patterns (see MagicForm). */
const DESKTOP_LAYOUT_MIN_WIDTH_PX = 992;

const useStyles = createUseStyles({
  viewport: {
    position: 'fixed',
    bottom: SUBSCRIPTION_VIEWPORT_BOTTOM_PX,
    left: 10,
    right: 0,
    display: 'flex',
    justifyContent: 'start',
    alignItems: 'flex-end',
    pointerEvents: 'none',
    zIndex: 10001,
    padding: '0 0px',
    [`@media (min-width: ${DESKTOP_LAYOUT_MIN_WIDTH_PX}px)`]: {
      bottom: 0,
    },
  },
  stackRoot: {
    position: 'relative',
    width: 'min(92vw, 280px)',
    minHeight: 168,
    pointerEvents: 'auto',
  },
  stackCard: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    borderRadius: 16,
    padding: '20px 40px 20px 22px',
    textAlign: 'center',
    backgroundColor: 'var(--ion-item-background, var(--ion-background-color, #2c2c2e))',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxSizing: 'border-box',
    transformOrigin: 'top center',
    transition:
      'transform 0.38s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease, box-shadow 0.28s ease',
  },
  stackCardFront: {
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
  },
  stackCardPeek: {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
  },
  accentBar: {
    position: 'absolute',
    left: 10,
    top: 22,
    bottom: 22,
    width: 3,
    borderRadius: 2,
  },
  cardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'var(--ion-text-color, #fff)',
  },
  cardBodyHtml: {
    marginTop: 8,
    marginBottom: 0,
    fontSize: 14,
    lineHeight: 1.4,
    color: 'var(--ion-color-medium-tint, #a8a8a8)',
    '& p': {
      margin: 0,
    },
  },
  actionsRow: {
    position: 'absolute',
    top: 14,
    right: 12,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  iconButton: {
    padding: 8,
    cursor: 'pointer',
    color: 'var(--ion-color-medium, #92949c)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    borderRadius: 8,
    '&:hover': {
      color: 'var(--ion-text-color, #fff)',
    },
  },
});

export interface InAppNotificationStackProps {
  items: SubscriptionStackItem[];
  pathname: string;
  onDismiss: (item: SubscriptionStackItem) => void;
  onNavigate: (url: string) => void;
}

const InAppNotificationStack: React.FC<InAppNotificationStackProps> = ({
  items,
  pathname,
  onDismiss,
  onNavigate,
}) => {
  const classes = useStyles();

  /** Newest first — `items` already sorted; back-of-stack first in DOM. */
  const renderItems = useMemo(
    () =>
      items
        .map((item, stackPosition) => ({ item, stackPosition }))
        .sort((a, b) => b.stackPosition - a.stackPosition),
    [items]
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={classes.viewport} aria-live="polite" aria-relevant="additions removals">
      <div className={classes.stackRoot}>
        {renderItems.map(({ item, stackPosition }) => {
          const isFront = stackPosition === 0;
          const translateY = stackPosition * STACK_OFFSET_Y;
          const scale = 1 - stackPosition * STACK_SCALE_STEP;
          const opacity = isFront ? 1 : Math.max(0.55, 0.92 - stackPosition * 0.14);

          const canNavigate = isFront && item.url && item.url !== pathname;

          return (
            <div
              key={item.id}
              className={`${classes.stackCard} ${
                isFront ? classes.stackCardFront : classes.stackCardPeek
              }`}
              style={{
                transform: `translateY(${translateY}px) scale(${scale})`,
                zIndex: 100 - stackPosition,
                opacity,
                pointerEvents: isFront ? 'auto' : 'none',
              }}
              onClick={isFront && canNavigate ? () => onNavigate(item.url as string) : undefined}
            >
              <p className={classes.cardTitle}>{item.subject}</p>
              <div
                className={classes.cardBodyHtml}
                dangerouslySetInnerHTML={{ __html: item.messageHtml }}
              />
              {isFront ? (
                <div
                  className={classes.actionsRow}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className={classes.iconButton}
                    aria-label="Dismiss notification"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(item);
                    }}
                  >
                    <IonIcon icon={closeIcon} />
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InAppNotificationStack;
